import React, { useState, CSSProperties } from 'react';
import Select, {
  components,
  ActionMeta,
  OptionProps,
  IndicatorProps,
  MultiValueProps,
  SelectOptionActionMeta,
  DeselectOptionActionMeta,
} from 'react-select';
import styled from 'styled-components';
import { theme } from '../../theme';
import { ChevronDownIcon, SvgComponent, CheckIcon } from '../../assets';
import {
  optionStyle,
  controlStyle,
  indicatorSeparatorStyle,
  dropdownIndicatorStyle,
  menuStyle,
  menuPortalStyle,
  singleValueStyle,
  valueContainerStyle,
  placeholderStyle,
} from './styles';
import { animationDuration, MenuComponent } from './DropDown';
import { UIText } from '../typography';

export type MultiDropDownProps = {
  onChange?: (
    actionMeta:
      | DeselectOptionActionMeta<MultiDropDownOption>
      | SelectOptionActionMeta<MultiDropDownOption>
  ) => void;
  onClick?: (e: Event) => void;
  options: MultiDropDownOption[];
  placeholder?: string | JSX.Element;
  selectedOptions?: MultiDropDownOption[];
};

export type MultiDropDownOption = {
  label: string;
  value: string;
  icon?: SvgComponent;
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledChevronDownIcon = styled(ChevronDownIcon)<{
  open?: boolean;
}>`
  height: 18px;
  width: 14px;
  transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
  transition: transform ${animationDuration}ms ease-in-out;
  margin: 0 ${theme.spacings[2].rem};
`;

const StyledSelect = styled(Select)`
  display: inline-block;
`;

const StyledValueContainertext = styled(UIText)`
  ${theme.typography.uiText.md};
`;

const StyledValueContainerDiv = styled.div`
  display: flex;
  align-item: center;
  align-content: center;
  justify-content: center;
`;

type SelectStylesProps = {
  isAnimateOpen: boolean;
};

const selectStyles = ({ isAnimateOpen }: SelectStylesProps) => ({
  option: (
    _: CSSProperties,
    { isFocused }: OptionProps<Record<string, unknown>, boolean>
  ) => ({
    ...optionStyle,
    fontWeight: 'bold',
    fontSize: '1rem',
    lineHeight: '1.25rem',
    cursor: isFocused ? 'pointer' : 'default',
    backgroundColor: isFocused ? theme.colors.neutrals.solidGrays[8] : 'unset',
  }),
  control: () => ({
    ...controlStyle,
    margin: '0 auto',
    width: '100%',
  }),
  indicatorSeparator: () => ({
    ...indicatorSeparatorStyle,
  }),
  dropdownIndicator: () => ({
    ...dropdownIndicatorStyle,
  }),
  menu: () => ({
    ...menuStyle,
    left: 0,
    minWidth: '100%',
    borderRadius: '10px',
    margin: `${theme.spacings[1].rem} 0 0 0`,
    maxHeight: isAnimateOpen ? '500px' : '0',
    transition: `max-height ${animationDuration}ms ease-in-out`,
  }),
  menuPortal: () => ({
    ...menuPortalStyle,
  }),
  container: (provided: CSSProperties) => ({
    ...provided,
    width: '100%',
  }),
  menuList: (provided: CSSProperties) => ({
    ...provided,
    padding: 0,
  }),
  singleValue: () => ({
    ...singleValueStyle,
    marginRight: 'auto',
    width: '90%',
    overflow: 'visible',
  }),
  valueContainer: () => ({
    ...valueContainerStyle,
    width: '90%',
    whiteSpace: 'nowrap',
    textOverflow: 'clip',
    padding: `${theme.spacings[3].rem}`,
  }),
  placeholder: () => ({
    ...placeholderStyle,
    justfiyContent: 'center',
  }),
});

const DropdownIndicator = (props: IndicatorProps<any, false>) => {
  return (
    <components.DropdownIndicator {...props}>
      <StyledChevronDownIcon open={props.selectProps.menuIsOpen} />
    </components.DropdownIndicator>
  );
};

const Menu = (props: any) => (
  <MenuComponent {...props}>{props.children}</MenuComponent>
);

const Option = (props: OptionProps<any, true>) => {
  return (
    <components.Option {...props}>
      {props.isSelected && (
        <CheckIcon
          width={16}
          style={{ fill: theme.colors.primary.graysGray }}
        />
      )}
      {props.data.label}
    </components.Option>
  );
};

const ValueContainer = ({ children, ...props }: MultiValueProps<any>) => {
  const { hasValue, getValue } = props;
  const selectedValues = getValue();
  let content;

  if (children && Array.isArray(children) && hasValue) {
    content = (
      <StyledValueContainerDiv>
        <StyledValueContainertext>
          {selectedValues.length === 1
            ? `${selectedValues[0].label}`
            : `${selectedValues.length} selected`}
        </StyledValueContainertext>
      </StyledValueContainerDiv>
    );
  } else if (Array.isArray(children)) {
    content = children[0];
  }

  return (
    <components.ValueContainer {...props}>
      {content}
      {Array.isArray(children) && children[1]}
    </components.ValueContainer>
  );
};

export const MultiDropDown: React.FC<MultiDropDownProps> = ({
  onChange,
  onClick,
  options,
  placeholder,
  selectedOptions,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimateOpen, setIsAnimateOpen] = useState(false);

  const handleChange = (
    _selectedOption: MultiDropDownOption[],
    actionMeta: ActionMeta<MultiDropDownOption>
  ) => {
    if (
      actionMeta.action === 'select-option' ||
      actionMeta.action === 'deselect-option'
    ) {
      onChange && onChange(actionMeta);
    }
  };

  const openMenu = () => {
    if (!isOpen) {
      setIsOpen(true);
      setIsAnimateOpen(true);
    }
  };

  const closeMenu = () => {
    if (isOpen) {
      setIsAnimateOpen(false);
      setTimeout(() => {
        setIsOpen(false);
      }, animationDuration);
    }
  };

  return (
    <Wrapper {...props}>
      <StyledSelect
        isMulti
        hideSelectedOptions={false}
        isClearable={false}
        menuIsOpen={isOpen}
        onMenuClose={closeMenu}
        onMenuOpen={openMenu}
        components={{
          Menu,
          Option,
          DropdownIndicator,
          ValueContainer,
        }}
        isSearchable={false}
        onChange={handleChange}
        onClick={onClick}
        options={options}
        styles={selectStyles({
          isAnimateOpen,
        })}
        value={selectedOptions}
        placeholder={placeholder}
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
      />
    </Wrapper>
  );
};
